import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { unref as _unref, isRef as _isRef, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, mergeProps as _mergeProps, normalizeClass as _normalizeClass, withCtx as _withCtx, createSlots as _createSlots, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment } from "vue";
const _hoisted_1 = { class: "flex flex-col items-center gap-1" };
const _hoisted_2 = ["id"];
const _hoisted_3 = ["id"];
const _hoisted_4 = {
    class: "flex flex-col gap-5 md:flex-row md:gap-8 md:min-h-125.5",
    "data-testid": "organisation-create-modal"
};
const _hoisted_5 = { class: "flex flex-col flex-1 min-w-0 gap-5" };
const _hoisted_6 = { class: "[&_i.ri-map-pin-2-line]:text-(--text-text-black-default)! dark:[&_i.ri-map-pin-2-line]:text-(--text-text-white-default)!" };
const _hoisted_7 = {
    key: 0,
    class: "rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-center gap-2"
};
const _hoisted_8 = { class: "md:w-92.5 shrink-0 md:relative" };
const _hoisted_9 = { class: "md:absolute md:inset-0 flex flex-col" };
import { AutoComplete, AutoCompleteAllResultsModal, Button, Modal, TextInput, } from "@eudonet/opal";
import { buildAutoCompleteLabels } from "@/shared/composables/autoCompleteLabels";
import { computed, nextTick, onUnmounted, reactive, ref, useId, watch } from "vue";
import { isNavigationFailure, NavigationFailureType, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { addOrganisationAddress, createNewOrganisation, } from "../../infrastructure/adapters/organisationService";
import { resolveEstablishmentNatureFromSirene } from "../../domain/establishment-nature";
import { parseAddressQuery } from "../../domain/address-utils";
import { isValidSiret, sanitizeSiretInput } from "../../domain/siret";
import { useAppToast } from "@/shared/composables/useAppToast";
import { useDeduplication, DEDUP_PANEL_MAX } from "../../composables/useDeduplication";
import { useSireneSearch } from "../../composables/useSireneSearch";
import { useAddressForm } from "../../composables/useAddressForm";
import { useWorkspaceCountry } from "@/shared/composables/useWorkspaceCountry";
import { toAddressLayout, withStreetLineOnly } from "@/shared/utils/addressFieldConfig";
import { useOrganisationPreview } from "../../composables/useOrganisationPreview";
import { logger } from "../../../../infrastructure/services/LoggerService";
import OrganisationPreviewDrawer from "./OrganisationPreviewDrawer.vue";
import OrganisationDedupPanel from "./OrganisationDedupPanel.vue";
import AddressDetailsModal from "./AddressDetailsModal.vue";
import OverwriteConfirmModal from "./OverwriteConfirmModal.vue";
export default /*@__PURE__*/ _defineComponent({
    __name: 'CreateOrganisationModal',
    props: /*@__PURE__*/ _mergeModels({
        redirectOnCreate: { type: Boolean, default: true },
        initialName: { default: "" }
    }, {
        "modelValue": { type: Boolean, ...{ default: false } },
        "modelModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(["created"], ["update:modelValue"]),
    setup(__props, { emit: __emit }) {
        const open = _useModel(__props, "modelValue");
        const props = __props;
        const emit = __emit;
        // ─── Dependencies ─────────────────────────────────────────────────────────────
        const router = useRouter();
        const { t } = useI18n();
        const { toastSuccess } = useAppToast();
        // ─── Composables (destructured for template auto-unwrap) ──────────────────────
        const { dedupState, dedupResults, dedupTitle, dedupHint, runDeduplication, reset: resetDedup, } = useDeduplication();
        const { sireneOptions, sireneTotalCount, sireneLoading, sireneUnavailable, showAllResultsModal, allSireneOptions, allSireneLoading, allSireneError, allResultsQuery, onLegalNameSearch, onSeeAllSireneResults, reset: resetSirene, } = useSireneSearch();
        const { address, city, postalCode, addressDirty, addressSuggestions, addressLoading, showAddressDetailsModal, addressDetailsSaved, addressDetails, onAddressSearch: _onAddressSearch, onAddressSelect, openAddressDetailsModal, saveAddressDetails, setFromSirene: setAddressFromSirene, reset: resetAddress, } = useAddressForm();
        const workspaceCountry = useWorkspaceCountry();
        // Local wrappers: keep address/city/postalCode in sync with free-text input
        // (OPAL clears modelValue as soon as the user types; @search re-syncs after the debounce).
        function onAddressSearch(query) {
            address.value = query;
            // Any keystroke diverges from the selected BAN suggestion — drop stale geo meta
            // so the save payload can't pair the new street with the previous selection's city/postal.
            city.value = "";
            postalCode.value = "";
            _onAddressSearch(query);
        }
        function onAddressClear() {
            address.value = "";
            city.value = "";
            postalCode.value = "";
        }
        const { showPreviewDrawer, previewOrg, previewLoading, previewName, openPreviewDrawer, reset: resetPreview, } = useOrganisationPreview();
        // ─── Refs ─────────────────────────────────────────────────────────────────────
        const legalNameWrapperRef = ref(null);
        const titleId = useId();
        const descriptionId = useId();
        watch(open, async (val) => {
            if (val) {
                // When the modal is opened pre-filled from an organisation picker (OP-6103),
                // the legal-name field already carries the searched term. Auto-focusing it
                // would satisfy the AutoComplete's `hasFocus && searchQuery` open condition
                // and pop an empty SIRENE dropdown over a value the user never just typed —
                // so skip the focus in the pre-fill case.
                if (props.initialName?.trim())
                    return;
                await nextTick();
                legalNameWrapperRef.value?.querySelector("input")?.focus();
            }
        });
        // ─── Form state ───────────────────────────────────────────────────────────────
        const form = reactive({
            legalName: "",
            siret: "",
            domainName: "",
        });
        const legalNameQuery = ref("");
        const canCreateAsNew = computed(() => {
            const q = legalNameQuery.value.trim();
            return q.length > 0 && /\D/.test(q);
        });
        // Pre-fill the legal name from the picker's search term so the user doesn't
        // retype what they already searched for (OP-6103). `immediate` is required: the
        // modal is mounted (v-if) only once it's first shown, with `open` already true,
        // so a non-immediate watcher would never see the false→true transition on the
        // first open. Mirrors `createAsNew` — seed the field AND run dedup so the submit
        // button isn't gated behind the `initialHint` state.
        watch(open, (val) => {
            if (!val)
                return;
            const seed = props.initialName?.trim();
            if (!seed)
                return;
            form.legalName = seed;
            legalNameQuery.value = seed;
            runDeduplication({
                legalName: seed,
                primaryRegistrationNumber: form.siret.replace(/\s/g, "") || undefined,
                domainName: form.domainName.trim() || undefined,
                city: city.value.trim() || undefined,
            });
            nextTick(cancelScheduledDedup);
        }, { immediate: true });
        // ─── SIRENE selection state ───────────────────────────────────────────────────
        const selectedSireneResult = ref(null);
        const pendingOverwrite = ref(null);
        function applySireneResult(r) {
            form.legalName = r.legalName;
            legalNameQuery.value = r.legalName;
            if (r.siret)
                form.siret = r.siret;
            setAddressFromSirene(r.address ?? "", r.city ?? "", r.postalCode ?? "");
            selectedSireneResult.value = r;
            pendingOverwrite.value = null;
        }
        function acceptOverwrite() {
            if (!pendingOverwrite.value)
                return;
            applySireneResult(pendingOverwrite.value.result);
            nextTick(cancelScheduledDedup);
        }
        function cancelOverwrite() {
            pendingOverwrite.value = null;
        }
        function onSireneSelect(option) {
            const r = option.meta?._sireneResult;
            if (!r)
                return;
            const conflictingFields = [];
            const newSiret = r.siret ?? "";
            if (form.siret.trim() && newSiret && form.siret.replace(/\s/g, "") !== newSiret) {
                conflictingFields.push("siret");
            }
            const newAddress = option.meta?.address ?? "";
            if (address.value.trim() && newAddress && address.value.trim() !== newAddress) {
                conflictingFields.push("address");
            }
            if (conflictingFields.length > 0) {
                pendingOverwrite.value = { result: r, conflictingFields };
            }
            else {
                applySireneResult(r);
            }
            runDeduplication({
                legalName: r.legalName,
                primaryRegistrationNumber: r.siret ?? undefined,
                domainName: form.domainName.trim() || undefined,
                city: r.city ?? undefined,
            });
            // applySireneResult mutates form fields, which triggers the legalName/siret watchers that
            // schedule a debounced dedup call. Cancel that timer here, after watchers have flushed.
            nextTick(cancelScheduledDedup);
        }
        // AutoCompleteAllResultsModal is generic over `AutoCompleteOption` so its
        // `confirm` event types as the wider parent. At runtime we always pass it
        // `SireneAutoCompleteOption[]` (via `allSireneOptions`), so the cast back is
        // safe — the same object identity flows through.
        function handleSireneAllResultsConfirm(option) {
            const sireneOpt = option;
            sireneOptions.value = [sireneOpt];
            onSireneSelect(sireneOpt);
        }
        function createAsNew() {
            form.legalName = legalNameQuery.value;
            runDeduplication({
                legalName: legalNameQuery.value.trim(),
                primaryRegistrationNumber: form.siret.replace(/\s/g, "") || undefined,
                domainName: form.domainName.trim() || undefined,
                city: city.value.trim() || undefined,
            });
            // Setting form.legalName above triggers the watcher that schedules a debounced
            // dedup; cancel it after watchers flush so we don't fire the API a second time.
            nextTick(cancelScheduledDedup);
        }
        // ─── Domain name validation ───────────────────────────────────────────────────
        const DOMAIN_NAME_REGEX = /^([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
        // Empty is allowed (domain is optional); when populated, must match a strict
        // hostname pattern.
        const isDomainValid = computed(() => !form.domainName || DOMAIN_NAME_REGEX.test(form.domainName));
        // Set to true on blur, and cleared again only once the field is emptied — NOT on
        // every keystroke. `isDomainValid` gates the submit button live, so clearing this on
        // each edit hid the reason while the button stayed disabled: the form blocked itself
        // with nothing on screen to say why (OP-7486). Everything the ticket lists as missing
        // — message, red border, `aria-invalid`, `role="alert"` — is that one empty `error`,
        // since the OPAL TextInput derives all four from it.
        const domainTouched = ref(false);
        // Server-side rejection (BFF "DomainName" field error) bypasses the touched
        // gate so the user sees the cause of the failed submit.
        const domainServerError = ref("");
        const domainError = computed(() => {
            if (domainServerError.value)
                return domainServerError.value;
            return domainTouched.value && !isDomainValid.value
                ? t("organisation.create.validation.domainInvalid")
                : "";
        });
        function onDomainBlur() {
            domainTouched.value = true;
        }
        // The submit gate and the error message share ONE condition, so the button can never
        // be disabled for a reason that is not on screen (OP-7486). Gating `canSubmit` on the
        // bare `isDomainValid` broke that: the button reacted on the first keystroke while the
        // message waited for a blur, and a disabled button emits no events and does not move
        // focus — so a user who pastes an invalid domain, or prefixes one without leaving the
        // field, can click it forever and never learn why. `handleSubmit` marks the field
        // visited, so an attempt to submit reveals the reason just like a blur does.
        const domainBlocksSubmit = computed(() => domainTouched.value && !isDomainValid.value);
        watch(() => form.domainName, () => {
            domainServerError.value = "";
            // Un-touch only once the field is EMPTY, i.e. genuinely fresh again — never on a
            // merely-valid intermediate value. Typing "https://" in front of "eudonet.com"
            // walks through "heudonet.com", "hteudonet.com" … which all pass the hostname
            // regex, so an "un-touch when valid" rule fires mid-keystroke and the message is
            // already suppressed by the time the value turns invalid. That is the ticket's
            // own repro, and it survives a per-keystroke check but not a bulk `setValue`.
            if (!form.domainName)
                domainTouched.value = false;
        });
        // Dedup is intentionally NOT triggered while the user types the legal name —
        // only on (1) autocomplete selection, (2) "Créer …" (createAsNew), and (3) edits
        // to the other form fields (siret / domainName / city). Keystroke-driven dedup
        // here races with the synchronous call in `onSireneSelect`: under dev-grade
        // network latency the older keystroke call returns *after* the selection call
        // and wipes the matches (see OP-5319).
        watch(() => form.legalName, (val) => {
            if (!val) {
                resetDedup();
                selectedSireneResult.value = null;
            }
        });
        // ─── Re-run dedup when fields change ─────────────────────────────────────────
        let dedupDebounceTimer = null;
        function cancelScheduledDedup() {
            if (dedupDebounceTimer) {
                clearTimeout(dedupDebounceTimer);
                dedupDebounceTimer = null;
            }
        }
        function scheduleDeduplication() {
            if (!form.legalName.trim())
                return;
            cancelScheduledDedup();
            dedupDebounceTimer = setTimeout(() => {
                runDeduplication({
                    legalName: form.legalName.trim(),
                    primaryRegistrationNumber: form.siret.replace(/\s/g, "") || undefined,
                    domainName: form.domainName.trim() || undefined,
                    city: city.value.trim() || undefined,
                });
            }, 500);
        }
        watch(() => form.siret, scheduleDeduplication);
        watch(() => form.domainName, scheduleDeduplication);
        watch(city, scheduleDeduplication);
        onUnmounted(cancelScheduledDedup);
        // ─── SIRET ────────────────────────────────────────────────────────────────────
        watch(() => form.siret, (val) => {
            const sanitized = sanitizeSiretInput(val);
            if (sanitized !== val)
                form.siret = sanitized;
        });
        const siretDigits = computed(() => form.siret.replace(/\s/g, ""));
        // Empty is allowed (SIRET is optional); when populated, the full 14 digits must
        // pass the INSEE checksum — partial input or 14 arbitrary digits both fail.
        const siretIsValid = computed(() => siretDigits.value.length === 0 || isValidSiret(siretDigits.value));
        // Stays false until the user leaves the SIRET field for the first time so we
        // don't flag "incomplete" or "not-yet-checksummed" input while they're still
        // typing. Once true, the error reflects validity on every keystroke so a
        // correction clears it immediately.
        const siretTouched = ref(false);
        const siretError = computed(() => siretTouched.value && !siretIsValid.value
            ? t("organisation.create.validation.PrimaryRegistrationNumberInvalid")
            : "");
        const siretIcon = computed(() => {
            if (!form.siret)
                return undefined;
            if (siretIsValid.value)
                return "ri-checkbox-circle-line";
            // Mirror the error display: hide the warning glyph until the field has been
            // blurred at least once so it doesn't flash while the user is mid-input.
            return siretTouched.value ? "ri-error-warning-line" : undefined;
        });
        function onSiretBlur() {
            siretTouched.value = true;
            if (!form.siret)
                return;
            const clean = siretDigits.value;
            if (/^\d{14}$/.test(clean)) {
                form.siret = `${clean.slice(0, 3)} ${clean.slice(3, 6)} ${clean.slice(6, 9)} ${clean.slice(9)}`;
            }
        }
        // ─── Raison sociale (legal name) ──────────────────────────────────────────────
        const legalNameError = computed(() => sireneUnavailable.value ? t("organisation.create.sireneUnavailable") : "");
        // ─── Navigation ───────────────────────────────────────────────────────────────
        async function goToOrganisation(id) {
            // The fiche pages guard route-leave behind an UnsavedChangesModal
            // (useSaveOrchestrator): the push below can be parked while the user decides,
            // then refused ("Rester"). Close the creation flow only once the navigation is
            // confirmed — closing it up-front left the user stranded on the host modal
            // (address / donation / relation…) with the fiche never opening, and threw
            // away their in-progress creation when they chose to stay.
            // Carry the creation context in the query so the fiche's back link can return to this
            // modal instead of the table view (OP-7384) — only for the top-level entry point
            // (`redirectOnCreate`); embedded callers (donation/address/relation pickers) have no
            // OrganisationList to return to.
            let refused;
            try {
                refused = await router.push({
                    name: "Organisation",
                    params: { id },
                    query: props.redirectOnCreate
                        ? {
                            fromOrganisationCreate: "1",
                            // On the overwrite-conflict path (onSireneSelect), form.legalName is never set —
                            // applySireneResult is skipped until the user resolves pendingOverwrite — so fall
                            // back to the raw typed term (legalNameQuery) instead of reopening the modal blank.
                            legalName: form.legalName.trim() || legalNameQuery.value.trim() || undefined,
                        }
                        : undefined,
                });
            }
            catch (error) {
                logger.error("Navigation to the organisation fiche failed", error);
                return;
            }
            // A duplicated failure means the target fiche is already on screen — a success
            // for this flow, so the creation modal must still close. Only a guard refusal
            // (aborted/cancelled) keeps the stack open.
            if (refused && !isNavigationFailure(refused, NavigationFailureType.duplicated))
                return;
            resetForm();
            open.value = false;
        }
        // ─── CTA state ────────────────────────────────────────────────────────────────
        const showAddressDetailsLink = computed(() => form.legalName.trim().length > 0 || address.value.trim().length > 0);
        // SIRET is intentionally NOT a submit-gating condition: non-French numbers
        // (NEQ CA, KVK NL, …) flow through this field and would fail the INSEE
        // checksum, so an invalid SIRET only surfaces a warning icon + message on blur
        // while the button stays active as long as the other fields are valid.
        const canSubmit = computed(() => form.legalName.trim().length > 0 &&
            !domainBlocksSubmit.value &&
            dedupState.value !== "initialHint" &&
            dedupState.value !== "checking" &&
            dedupState.value !== "duplicateFound");
        // ─── Handlers ─────────────────────────────────────────────────────────────────
        const submitting = ref(false);
        const serverError = ref(null);
        function resetForm() {
            form.legalName = "";
            form.siret = "";
            form.domainName = "";
            legalNameQuery.value = "";
            serverError.value = null;
            domainServerError.value = "";
            domainTouched.value = false;
            siretTouched.value = false;
            selectedSireneResult.value = null;
            pendingOverwrite.value = null;
            resetSirene();
            resetAddress();
            resetDedup();
            resetPreview();
        }
        function handleCancel() {
            resetForm();
            open.value = false;
        }
        async function handleSubmit() {
            // Attempting to submit counts as visiting the domain field: an invalid value that
            // was never blurred must explain itself here rather than silently do nothing.
            domainTouched.value = true;
            if (!canSubmit.value)
                return;
            submitting.value = true;
            serverError.value = null;
            try {
                const s = selectedSireneResult.value;
                const coordsKnown = !addressDirty.value;
                const id = await createNewOrganisation({
                    legalName: form.legalName.trim(),
                    primaryRegistrationNumber: form.siret.replace(/\s/g, "") || undefined,
                    domainName: form.domainName.trim() || undefined,
                    tradeName: s?.tradeName ?? undefined,
                    latitude: coordsKnown ? (s?.latitude ?? undefined) : undefined,
                    longitude: coordsKnown ? (s?.longitude ?? undefined) : undefined,
                    activityCode: s?.activityCode ?? undefined,
                    activityLabel: s?.activityLabel ?? undefined,
                    legalForm: s?.legalForm ?? undefined,
                    companyCategory: s?.companyCategory ?? undefined,
                    employeeRange: s?.employeeRange ?? undefined,
                    employeeRangeYear: s?.employeeRangeYear ?? undefined,
                    legalStatus: s?.legalStatus ?? undefined,
                    registrationDate: s?.registrationDate ?? undefined,
                    establishmentNature: resolveEstablishmentNatureFromSirene(s?.isHeadquarters),
                });
                if (address.value.trim()) {
                    try {
                        const ad = addressDetails;
                        const saved = addressDetailsSaved.value;
                        const parsed = parseAddressQuery(address.value.trim());
                        const street = parsed.line1?.trim() || address.value.trim();
                        // The SIRENE street fields are not sent: its street goes through line1, like a
                        // typed one, and the workspace layout decides whether a house number is split out.
                        const f = toAddressLayout(saved ? ad : withStreetLineOnly(ad, street, workspaceCountry.value), workspaceCountry.value);
                        await addOrganisationAddress(id, {
                            type: "Headquarters",
                            addressLine: f.line1 || undefined,
                            addressLine2: f.line2 || undefined,
                            addressLine3: f.line3 || undefined,
                            houseNumber: f.houseNumber || undefined,
                            houseNumberSuffix: f.houseNumberSuffix || undefined,
                            region: f.region || undefined,
                            postalCode: ad.postalCode || postalCode.value || (s?.postalCode ?? undefined),
                            city: city.value || (s?.city ?? undefined),
                            // useAddressForm seeds it with the workspace default; the user's choice replaces it.
                            country: ad.country || undefined,
                            latitude: coordsKnown ? (s?.latitude ?? undefined) : undefined,
                            longitude: coordsKnown ? (s?.longitude ?? undefined) : undefined,
                            isPrimary: true,
                        });
                    }
                    catch (addrError) {
                        logger.error("Failed to create organisation address", addrError);
                        serverError.value = t("organisation.create.errors.serverError");
                        return;
                    }
                }
                emit("created", id, form.legalName.trim());
                resetForm();
                open.value = false;
                toastSuccess("organisation.create.saveSuccess");
                // Skip the redirect when nested inside another flow (e.g. donation modal)
                // — the parent listens to `created` and resumes its own work. Without the
                // gate, navigating away would abandon the parent form's in-progress data.
                if (props.redirectOnCreate) {
                    // Fire-and-forget is deliberate here: the organisation is persisted and the
                    // modal already closed, so a guard refusal just leaves the user where they
                    // were. The catch only prevents an unhandled rejection on a failed push.
                    router.push({ name: "Organisation", params: { id } }).catch((error) => {
                        logger.error("Navigation to the organisation fiche failed", error);
                    });
                }
            }
            catch (error) {
                const responseData = error?.response
                    ?.data;
                if (responseData?.errorResult && typeof responseData.errorResult === "object") {
                    const fieldErrors = responseData.errorResult;
                    if (fieldErrors["DomainName"]?.length || fieldErrors["domainName"]?.length) {
                        domainServerError.value = t("organisation.create.validation.domainInvalid");
                        return;
                    }
                }
                serverError.value = t("organisation.create.errors.serverError");
            }
            finally {
                submitting.value = false;
            }
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createVNode(OrganisationPreviewDrawer, {
                    modelValue: _unref(showPreviewDrawer),
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => (_isRef(showPreviewDrawer) ? (showPreviewDrawer).value = $event : null)),
                    org: _unref(previewOrg),
                    "fallback-title": _unref(previewName),
                    loading: _unref(previewLoading),
                    onGoToOrganisation: goToOrganisation
                }, null, 8 /* PROPS */, ["modelValue", "org", "fallback-title", "loading"]),
                _createVNode(_unref(Modal), {
                    modelValue: open.value,
                    "onUpdate:modelValue": [
                        _cache[9] || (_cache[9] = ($event) => ((open).value = $event)),
                        _cache[10] || (_cache[10] = (v) => !v && handleCancel())
                    ],
                    size: "lg",
                    "show-close-button": true,
                    "show-header-border": false,
                    "show-footer-border": false,
                    "footer-position": "right",
                    "border-gradient": "linear-gradient(var(--color-mediumslateblue), var(--color-mediumslateblue))",
                    "content-class": "shadow-[0px_1px_50px_0px_rgba(0,0,0,0.1)]! md:max-h-[740px]!",
                    "aria-labelledby": _unref(titleId),
                    "aria-describedby": _unref(descriptionId)
                }, {
                    header: _withCtx(() => [
                        _createElementVNode("div", _hoisted_1, [
                            _createElementVNode("span", {
                                id: _unref(titleId),
                                class: "text-xl font-bold text-(--text-text-black-default) dark:text-(--text-text-white-default)"
                            }, _toDisplayString(_ctx.$t("organisation.create.title")), 9 /* TEXT, PROPS */, _hoisted_2),
                            _createElementVNode("p", {
                                id: _unref(descriptionId),
                                class: "text-sm font-normal leading-5 text-center text-(--text-text-heading) dark:text-(--text-text-white-default)"
                            }, [
                                _createTextVNode(_toDisplayString(_ctx.$t("organisation.create.subtitle")) + " ", 1 /* TEXT */),
                                _cache[13] || (_cache[13] = _createElementVNode("br", null, null, -1 /* CACHED */)),
                                _createTextVNode(" " + _toDisplayString(_ctx.$t("organisation.create.requiredFieldsHint")), 1 /* TEXT */)
                            ], 8 /* PROPS */, _hoisted_3)
                        ])
                    ]),
                    footer: _withCtx(() => [
                        _createVNode(_unref(Button), {
                            variant: "primary",
                            "icon-left": "ri-check-line",
                            "data-testid": "organisation-create-submit",
                            label: _ctx.$t('organisation.create.submit'),
                            disabled: !canSubmit.value,
                            loading: submitting.value,
                            onClick: handleSubmit
                        }, null, 8 /* PROPS */, ["label", "disabled", "loading"])
                    ]),
                    default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_4, [
                            _createElementVNode("div", _hoisted_5, [
                                _createElementVNode("div", {
                                    ref_key: "legalNameWrapperRef",
                                    ref: legalNameWrapperRef
                                }, [
                                    _createVNode(_unref(AutoComplete), _mergeProps(_unref(buildAutoCompleteLabels)(_unref(t)), {
                                        modelValue: form.legalName,
                                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((form.legalName) = $event)),
                                        "data-testid": "organisation-create-legal-name",
                                        "icon-left": "ri-building-line",
                                        label: _ctx.$t('organisation.create.fields.legalName'),
                                        placeholder: _ctx.$t('organisation.create.fields.legalNamePlaceholder'),
                                        options: _unref(sireneOptions),
                                        loading: _unref(sireneLoading),
                                        required: true,
                                        hint: _ctx.$t('organisation.create.fields.legalNameHint'),
                                        error: legalNameError.value || undefined,
                                        "full-width": "",
                                        "see-all-label": _unref(sireneTotalCount) > 5 ? _ctx.$t('organisation.create.seeAllResults', { count: _unref(sireneTotalCount) }) : undefined,
                                        "create-label": canCreateAsNew.value ? _ctx.$t('common.createAsNewOrganisation', { name: legalNameQuery.value }) : undefined,
                                        onInput: _cache[2] || (_cache[2] = (e) => (legalNameQuery.value = e.target.value)),
                                        onClear: _cache[3] || (_cache[3] = ($event) => (legalNameQuery.value = '')),
                                        onSearch: _unref(onLegalNameSearch),
                                        onSelect: onSireneSelect,
                                        onSeeAll: _cache[4] || (_cache[4] = ($event) => (_unref(onSeeAllSireneResults)())),
                                        onCreate: _cache[5] || (_cache[5] = ($event) => (createAsNew()))
                                    }), null, 16 /* FULL_PROPS */, ["modelValue", "label", "placeholder", "options", "loading", "hint", "error", "see-all-label", "create-label", "onSearch"])
                                ], 512 /* NEED_PATCH */),
                                _createElementVNode("div", _hoisted_6, [
                                    _createVNode(_unref(AutoComplete), _mergeProps(_unref(buildAutoCompleteLabels)(_unref(t)), {
                                        modelValue: _unref(address),
                                        "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event) => (_isRef(address) ? (address).value = $event : null)),
                                        label: _ctx.$t('organisation.create.fields.address'),
                                        placeholder: _ctx.$t('organisation.create.fields.addressPlaceholder'),
                                        options: _unref(addressSuggestions),
                                        loading: _unref(addressLoading),
                                        "show-add-more-details": showAddressDetailsLink.value,
                                        "add-more-details-label": _ctx.$t('organisation.create.fields.addressAddMoreDetails'),
                                        "keep-input-on-edit": "",
                                        "full-width": "",
                                        onSearch: onAddressSearch,
                                        onClear: onAddressClear,
                                        onSelect: _unref(onAddressSelect),
                                        onAddMoreDetails: _unref(openAddressDetailsModal)
                                    }), null, 16 /* FULL_PROPS */, ["modelValue", "label", "placeholder", "options", "loading", "show-add-more-details", "add-more-details-label", "onSelect", "onAddMoreDetails"])
                                ]),
                                _createVNode(_unref(TextInput), {
                                    modelValue: form.siret,
                                    "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event) => ((form.siret) = $event)),
                                    label: _ctx.$t('organisation.create.fields.siret'),
                                    placeholder: _ctx.$t('organisation.create.fields.siretPlaceholder'),
                                    hint: _ctx.$t('organisation.create.fields.siretHint'),
                                    error: siretError.value || undefined,
                                    inputmode: "numeric",
                                    autocomplete: "off",
                                    "full-width": "",
                                    onBlur: onSiretBlur
                                }, _createSlots({ _: 2 /* DYNAMIC */ }, [
                                    (siretIcon.value)
                                        ? {
                                            name: "iconRight",
                                            fn: _withCtx(() => [
                                                _createElementVNode("i", {
                                                    class: _normalizeClass([siretIcon.value, 'text-sm text-(--text-text-body-subtle)'])
                                                }, null, 2 /* CLASS */)
                                            ]),
                                            key: "0"
                                        }
                                        : undefined
                                ]), 1032 /* PROPS, DYNAMIC_SLOTS */, ["modelValue", "label", "placeholder", "hint", "error"]),
                                _createVNode(_unref(TextInput), {
                                    modelValue: form.domainName,
                                    "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event) => ((form.domainName) = $event)),
                                    label: _ctx.$t('organisation.create.fields.domainName'),
                                    placeholder: _ctx.$t('organisation.create.fields.domainNamePlaceholder'),
                                    hint: _ctx.$t('organisation.create.fields.domainNameHint'),
                                    error: domainError.value || undefined,
                                    "full-width": "",
                                    onBlur: onDomainBlur
                                }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder", "hint", "error"]),
                                (serverError.value)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                                        _cache[14] || (_cache[14] = _createElementVNode("i", { class: "ri-error-warning-line text-base shrink-0" }, null, -1 /* CACHED */)),
                                        _createTextVNode(" " + _toDisplayString(serverError.value), 1 /* TEXT */)
                                    ]))
                                    : _createCommentVNode("v-if", true)
                            ]),
                            _createElementVNode("div", _hoisted_8, [
                                _createElementVNode("div", _hoisted_9, [
                                    _createVNode(OrganisationDedupPanel, {
                                        state: _unref(dedupState),
                                        results: _unref(dedupResults),
                                        title: _unref(dedupTitle),
                                        hint: _unref(dedupHint),
                                        "max-results": _unref(DEDUP_PANEL_MAX),
                                        onPreview: _unref(openPreviewDrawer),
                                        onGoTo: goToOrganisation
                                    }, null, 8 /* PROPS */, ["state", "results", "title", "hint", "max-results", "onPreview"])
                                ])
                            ])
                        ])
                    ]),
                    _: 1 /* STABLE */
                }, 8 /* PROPS */, ["modelValue", "aria-labelledby", "aria-describedby"]),
                _createVNode(AddressDetailsModal, {
                    modelValue: _unref(showAddressDetailsModal),
                    "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event) => (_isRef(showAddressDetailsModal) ? (showAddressDetailsModal).value = $event : null)),
                    "initial-details": _unref(addressDetails),
                    onSave: _unref(saveAddressDetails)
                }, null, 8 /* PROPS */, ["modelValue", "initial-details", "onSave"]),
                _createVNode(OverwriteConfirmModal, {
                    "model-value": !!pendingOverwrite.value,
                    onAccept: acceptOverwrite,
                    onCancel: cancelOverwrite
                }, null, 8 /* PROPS */, ["model-value"]),
                _createVNode(_unref(AutoCompleteAllResultsModal), {
                    modelValue: _unref(showAllResultsModal),
                    "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event) => (_isRef(showAllResultsModal) ? (showAllResultsModal).value = $event : null)),
                    options: _unref(allSireneOptions),
                    loading: _unref(allSireneLoading),
                    error: _unref(allSireneError),
                    title: _ctx.$t('organisation.create.allResults.title', {
                        count: _unref(sireneTotalCount),
                        query: _unref(allResultsQuery),
                    }),
                    subtitle: _ctx.$t('organisation.create.allResults.subtitle'),
                    "close-label": _ctx.$t('organisation.create.allResults.close'),
                    "select-label": _ctx.$t('organisation.create.allResults.select'),
                    "load-error-label": _ctx.$t('organisation.create.allResults.loadError'),
                    "empty-label": _ctx.$t('organisation.create.allResults.empty'),
                    onConfirm: handleSireneAllResultsConfirm
                }, null, 8 /* PROPS */, ["modelValue", "options", "loading", "error", "title", "subtitle", "close-label", "select-label", "load-error-label", "empty-label"])
            ], 64 /* STABLE_FRAGMENT */));
        };
    }
});
